'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { connect } from 'react-redux';
import { debounce, pick } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentOptions from 'DesignEditorsUI/components/customWidgetEditor/sidebar/fieldEdit/contentOptions/ContentOptions';
import FormCardSection from 'DesignEditorsUI/components/shared/formCard/FormCardSection';
import FormCardContainer from 'DesignEditorsUI/components/shared/formCard/FormCardContainer';
import FormCardBody from 'DesignEditorsUI/components/shared/formCard/FormCardBody';
import FormCardWrapper from 'DesignEditorsUI/components/shared/formCard/FormCardWrapper';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UILink from 'UIComponents/link/UILink';
import Toolbar from 'DesignEditorsUI/components/shared/Toolbar';
import IDECodeMirror from 'DesignEditorsUI/components/shared/IDECodeMirror';
import { getUserHasReadAccessOnly } from 'DesignData/selectors/auth';
import * as CustomWidgetBufferActions from 'DesignData/actions/CustomWidgetBuffers';
import * as CustomWidgetEditorOptionsActions from 'DesignData/actions/CustomWidgetEditorOptions';
import * as customWidgetEditorOptionsSelectors from 'DesignData/selectors/customWidgetEditorOptions';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import { MIN, CHOICES, DEFAULT } from 'DesignData/enums/CustomWidgetFieldAttributes';
import { HTML, HUBL } from 'ContentUtils/constants/CustomWidgetFieldTypes';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.${key}`;

function makeMapStateToProps() {
  const getIsEmailOrAnyType = customWidgetBuffersSelectors.makeGetIsEmailOrAnyType();
  const getIsReadOnly = customWidgetBuffersSelectors.makeGetIsReadOnly();
  return (state, props) => {
    const getCustomWidgetBuffer = customWidgetBuffersSelectors.makeGetCustomWidgetBuffer();
    const customWidgetBuffer = getCustomWidgetBuffer(state, props);
    const fieldErrors = customWidgetEditorOptionsSelectors.getFieldErrors(state, props);
    const fieldWarnings = customWidgetEditorOptionsSelectors.getFieldWarnings(state, props);
    const isReadOnly = getIsReadOnly(state, props);
    const isEditingGlobalContent = customWidgetEditorOptionsSelectors.getIsEditingGlobalContent(state, props);
    return Object.assign({
      customWidgetId: props.customWidgetId,
      isReadOnly: getUserHasReadAccessOnly(state) || isReadOnly || isEditingGlobalContent,
      fieldErrors: pick(fieldErrors, [MIN, CHOICES, DEFAULT]),
      fieldWarnings: pick(fieldWarnings, [MIN, CHOICES, DEFAULT]),
      isEmailOrAnyType: getIsEmailOrAnyType(state, props),
      isV1: customWidgetBuffer ? customWidgetBuffer.schemaVersion === 1 : false
    }, customWidgetEditorOptionsSelectors.getSelectedField(state, props));
  };
}

const mapDispatchToProps = {
  editField: CustomWidgetBufferActions.editField,
  setFullScreenEditorOpen: CustomWidgetEditorOptionsActions.setFullScreenEditorOpen
};

class FullScreenFieldEditor extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      field: this.props.field
    };

    this.editField = changes => {
      const {
        customWidgetId
      } = this.props;
      const {
        field
      } = this.state;
      const updatedField = field.merge(changes);
      this.setState({
        field: updatedField
      });
      this.debouncedEditField(customWidgetId, updatedField);
    };

    this.debouncedEditField = debounce(function debouncedEditField(customWidgetId, updatedField) {
      this.props.editField(customWidgetId, updatedField);
    }, 1000);

    this.closeEditor = () => {
      //HACK this is to prevent the "t.removeEventListener is not a function" error
      // that happens when the user clicks "back" while a tinymce modal is open. the
      // error gets thrown by the tether lib, which we do not have control over. This
      // gives tinymce a chance to unfocus before performing our nav. We can remove this
      // when tinymce migrates to the new internal rewrite of tether.
      // Error: https://sentry.hubteam.com/sentry/designmanagerui/issues/12039780/
      setTimeout(() => this.props.setFullScreenEditorOpen(this.props.customWidgetId, false), 0);
    };

    this.onSourceChange = data => {
      const {
        value
      } = data;
      if (value == null) return;
      this.editField({
        [DEFAULT]: value
      });
    };
  }

  renderBreadcrumbs() {
    return /*#__PURE__*/_jsx(UIBreadcrumbs, {
      className: "m-top-0",
      flush: true,
      children: /*#__PURE__*/_jsx(UILink, {
        "data-test-id": "full-screen-field-editor-back-button",
        onClick: this.closeEditor,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('backToEditor')
        })
      })
    });
  }

  renderFullScreenCodeEditor() {
    const {
      field
    } = this.props;
    const defaultValue = field[DEFAULT];
    const fieldValue = field.occurrence && Array.isArray(defaultValue) ? defaultValue && defaultValue[0] : defaultValue;
    return /*#__PURE__*/_jsxs("div", {
      className: "custom-widget-field-editor-full-screen-editor",
      children: [/*#__PURE__*/_jsx(Toolbar, {
        leftContent: this.renderBreadcrumbs()
      }), /*#__PURE__*/_jsx(IDECodeMirror, {
        sessionId: field.id,
        mode: field.type === HTML ? 'htmlmixed' : 'hubl-html',
        height: "auto",
        autofocus: true,
        value: fieldValue || '',
        onChange: this.onSourceChange,
        enableHublValidation: false
      }, field.id)]
    });
  }

  render() {
    const {
      customWidgetId,
      field,
      fieldErrors,
      fieldWarnings,
      isReadOnly,
      isV1,
      isEmailOrAnyType
    } = this.props; // See #5507 - Field is null when using undo button

    if (!field) {
      this.closeEditor();
      return null;
    }

    if (field.type === HTML || field.type === HUBL) {
      return this.renderFullScreenCodeEditor();
    }

    return /*#__PURE__*/_jsx(FormCardContainer, {
      style: {
        height: '100%',
        position: 'absolute'
      },
      children: /*#__PURE__*/_jsxs(FormCardWrapper, {
        children: [/*#__PURE__*/_jsx(FormCardSection, {
          children: this.renderBreadcrumbs()
        }), /*#__PURE__*/_jsx(FormCardBody, {
          children: /*#__PURE__*/_jsx(ContentOptions, {
            customWidgetId: customWidgetId,
            editField: this.editField,
            field: field,
            fieldErrors: fieldErrors,
            fieldWarnings: fieldWarnings,
            readOnly: isReadOnly,
            isV1: isV1,
            isEmailOrAnyType: isEmailOrAnyType
          })
        })]
      })
    });
  }

}

FullScreenFieldEditor.propTypes = {
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField),
  fieldErrors: PropTypes.object.isRequired,
  fieldWarnings: PropTypes.object.isRequired,
  setFullScreenEditorOpen: PropTypes.func.isRequired,
  isEmailOrAnyType: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  isV1: PropTypes.bool.isRequired
};
FullScreenFieldEditor.defaultProps = {
  customWidgetIcon: null,
  field: null,
  isNew: false
};
export default connect(makeMapStateToProps, mapDispatchToProps)(FullScreenFieldEditor);